import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import StatsCard from "@/components/dashboard/StatsCard";
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  Percent,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import {
  fetchRevenueAnalytics,
  RevenueAnalytics,
  DateRange,
} from "@/api/business-analytics";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface RevenueTabProps {
  dateRange: DateRange;
}

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// Custom tooltip component that supports dark mode
interface TooltipPayload {
  color: string;
  name: string;
  value: string | number;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const RevenueTab: React.FC<RevenueTabProps> = ({ dateRange }) => {
  const {
    data: analytics,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      "revenueAnalytics",
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ],
    queryFn: () => fetchRevenueAnalytics(dateRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Hooks must be called unconditionally; compute memoized data with safe defaults
  const discountData = useMemo(
    () =>
      analytics
        ? [
            {
              name: "Actual Revenue",
              value: analytics.actualRevenue,
              color: "#10b981",
            },
            {
              name: "Referral Discounts",
              value: analytics.referralDiscounts,
              color: "#f59e0b",
            },
            {
              name: "Promo Discounts",
              value: analytics.promoDiscounts,
              color: "#ef4444",
            },
          ]
        : [],
    [analytics]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load revenue analytics. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No revenue data available for the selected date range.
        </AlertDescription>
      </Alert>
    );
  }

  const totalDiscounts = analytics.referralDiscounts + analytics.promoDiscounts;
  const discountPercentage =
    analytics.grossRevenue > 0
      ? (totalDiscounts / analytics.grossRevenue) * 100
      : 0;

  return (
    <div className="space-y-6">
      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Gross Revenue"
          value={`₹${analytics.grossRevenue.toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          trend={{
            value: Math.abs(analytics.revenueGrowthRate),
            isPositive: analytics.revenueGrowthRate >= 0,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Net Revenue"
          value={`₹${analytics.actualRevenue.toFixed(2)}`}
          icon={<CreditCard className="h-4 w-4" />}
          trend={{
            value: Math.abs(analytics.revenueGrowthRate),
            isPositive: analytics.revenueGrowthRate >= 0,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Total Discounts"
          value={`₹${totalDiscounts.toFixed(2)}`}
          icon={<Percent className="h-4 w-4" />}
          trend={{
            value: Number(Math.abs(discountPercentage).toFixed(2)),
            isPositive: false,
            period: "of gross revenue",
          }}
        />
        <StatsCard
          title="Growth Rate"
          value={`${analytics.revenueGrowthRate}%`}
          icon={<TrendingUp className="h-4 w-4" />}
          trend={{
            value: Math.abs(analytics.revenueGrowthRate),
            isPositive: analytics.revenueGrowthRate >= 0,
            period: "prev. period",
          }}
        />
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Breakdown</CardTitle>
            <CardDescription>
              Distribution of revenue vs discounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-pie-sector]:hover:brightness-110 [&_.recharts-pie-sector]:hover:cursor-pointer [&_.recharts-pie-sector]:transition-all [&_.recharts-pie-sector]:duration-200"
            >
              <PieChart>
                <Pie
                  data={discountData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ₹${value.toFixed(2)}`}
                  className="hover:brightness-110"
                >
                  {discountData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Plan Revenue Performance</CardTitle>
            <CardDescription>
              Revenue generated by each subscription plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
            >
              <RechartsBarChart
                data={analytics.planPerformance}
                layout="horizontal"
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" className="text-muted-foreground" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  className="text-muted-foreground"
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="revenue"
                  fill="#3b82f6"
                  className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                />
              </RechartsBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Health Check</CardTitle>
            <CardDescription>Key performance indicators</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Discount Rate
              </span>
              <span
                className={`text-sm font-medium ${
                  discountPercentage > 30
                    ? "text-red-600 dark:text-red-400"
                    : discountPercentage > 15
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-green-600 dark:text-green-400"
                }`}
              >
                {discountPercentage.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Revenue Efficiency
              </span>
              <span
                className={`text-sm font-medium ${
                  analytics.actualRevenue > analytics.grossRevenue * 0.8
                    ? "text-green-600 dark:text-green-400"
                    : "text-orange-600 dark:text-orange-400"
                }`}
              >
                {(
                  (analytics.actualRevenue / analytics.grossRevenue) *
                  100
                ).toFixed(1)}
                %
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Referral Impact</span>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                ₹{analytics.referralDiscounts.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Promo Impact</span>
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                ₹{analytics.promoDiscounts.toFixed(2)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Plan Performance Details</CardTitle>
            <CardDescription>
              Detailed breakdown of revenue by subscription plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.planPerformance.map((plan, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-4 h-4 rounded-full`}
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <div>
                      <h4 className="font-medium">{plan.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {plan.subscriptions} active subscriptions
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-lg">
                      ₹{plan.revenue.toFixed(2)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Avg: ₹{plan.avgPrice.toFixed(2)}
                    </p>
                    <p
                      className={`text-xs ${
                        plan.growth > 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {plan.growth > 0 ? "+" : ""}
                      {plan.growth}% growth
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Summary</CardTitle>
          <CardDescription>
            Overall financial performance for the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                Total Gross Revenue
              </h3>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{analytics.grossRevenue.toFixed(2)}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Before discounts
              </p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                Net Revenue
              </h3>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ₹{analytics.actualRevenue.toFixed(2)}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                After all discounts
              </p>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200">
                Revenue Loss
              </h3>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                ₹{totalDiscounts.toFixed(2)}
              </p>
              <p className="text-sm text-orange-600 dark:text-orange-400">
                {discountPercentage.toFixed(1)}% of gross revenue
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
